import React, { forwardRef, useEffect, useRef, useState } from "react";
import { Calculator } from "lucide-react";

import { Popover, PopoverContent, PopoverTrigger } from "../../../components/ui/popover";
import { calcular, numero, temConta } from "../lib/numeros";
import Calculadora from "./Calculadora";

const PERMITIDOS = /[^0-9.,+\-*/x×÷() ]/gi;
const TEM_OPERADOR = /[+\-*/x×÷()]/i;

// Campo de número da compra. Em dinheiro (prefixo R$) os dígitos entram pela direita,
// como numa maquininha: 3 → 0,03; 36 → 0,36; 3690 → 36,90. Digitar um sinal (+ − × ÷)
// vira conta livre (6*12, 107,64/6), que o Enter confirma.
const CampoConta = forwardRef(function CampoConta(
  { id, rotulo, valor, aoMudar, prefixo, sufixo, casas = 2, minimo, aoEnter, destaque, destaqueTotal, className = "", e2e }, ref,
) {
  const moeda = prefixo === "R$";
  const [foco, setFoco] = useState(false);
  const [texto, setTexto] = useState("");
  const [modoConta, setModoConta] = useState(false);
  const [calcAberta, setCalcAberta] = useState(false);
  const emitido = useRef(valor);
  const min = minimo ?? casas;
  const formatado = valor == null ? "" : numero(valor, casas, { minimo: min });
  const livre = foco && (!moeda || modoConta);

  // Valor trocado por fora com o campo em foco (ex.: mexeu no preço por pacote): mostra o novo.
  useEffect(() => {
    if (valor !== emitido.current) { emitido.current = valor; setModoConta(false); setTexto(formatado.replace(/\./g, "")); }
  }, [valor]); // eslint-disable-line react-hooks/exhaustive-deps

  const emitir = (v) => { emitido.current = v; aoMudar(v); };
  const conta = livre && temConta(texto);
  const resultado = conta ? calcular(texto) : null;

  const aoFim = (campo) => requestAnimationFrame(() => { const n = campo.value.length; campo.setSelectionRange(n, n); });

  const mudar = (t, campo) => {
    const limpo = t.replace(PERMITIDOS, "");
    if (moeda && !TEM_OPERADOR.test(limpo)) {
      // Máscara da direita pra esquerda: só os dígitos contam, os dois últimos são os centavos.
      setModoConta(false);
      const digitos = limpo.replace(/\D/g, "").slice(0, 12);
      setTexto(digitos ? numero(Number(digitos) / 100, 2) : "");
      if (campo) aoFim(campo); // o próximo dígito entra sempre no fim
      return emitir(digitos ? Number(digitos) / 100 : null);
    }
    if (moeda) setModoConta(true);
    setTexto(limpo);
    if (!limpo.trim()) return emitir(null);
    const v = calcular(limpo);
    if (v != null) emitir(Math.max(0, v));
  };

  const confirmar = () => {
    if (conta && resultado != null) { setTexto(numero(Math.max(0, resultado), casas, { minimo: min })); setModoConta(false); }
  };

  return (
    <div className={className}>
      <label htmlFor={id} className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">{rotulo}</label>
      <div className={`mt-1 flex items-center rounded-lg border transition-shadow focus-within:ring-2 focus-within:ring-ring/40 ${
        destaqueTotal ? "bg-primary/5 border-primary/40" : destaque ? "bg-background border-primary/50" : "bg-background"}`}>
        {prefixo && <span className="pl-2.5 text-sm font-medium text-muted-foreground">{prefixo}</span>}
        <input
          ref={ref} id={id} data-e2e={e2e} inputMode="decimal" autoComplete="off"
          value={foco ? texto : formatado}
          placeholder={moeda ? "0,00" : casas === 3 ? "0,000" : "0"}
          onFocus={(e) => {
            setFoco(true); setModoConta(false); setTexto(moeda ? formatado : formatado.replace(/\./g, ""));
            // Entrou no campo: o que já estava fica selecionado, então o primeiro dígito
            // recomeça do 0,00 e os seguintes entram pela direita.
            if (formatado) requestAnimationFrame(() => e.target.select());
          }}
          onBlur={() => { confirmar(); setFoco(false); }}
          onChange={(e) => mudar(e.target.value, e.target)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); confirmar(); aoEnter?.(); } }}
          className={`min-w-0 flex-1 h-12 bg-transparent px-2 font-semibold tabular-nums outline-none ${destaqueTotal ? "text-xl" : "text-base"}`}
        />
        {sufixo && <span className="pr-1 text-sm text-muted-foreground whitespace-nowrap">{sufixo}</span>}
        <Popover open={calcAberta} onOpenChange={setCalcAberta}>
          <PopoverTrigger asChild>
            <button type="button" title="Abrir calculadora" aria-label={`Calculadora para ${rotulo}`} data-e2e={e2e && `${e2e}-calc`}
              className="mr-1 h-8 w-8 shrink-0 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted flex items-center justify-center">
              <Calculator className="w-4 h-4" />
            </button>
          </PopoverTrigger>
          <PopoverContent align="end" className="w-auto p-3">
            {calcAberta && (
              <Calculadora inicial={valor} casas={casas}
                aoUsar={(v) => { emitir(Math.max(0, v)); setCalcAberta(false); }} />
            )}
          </PopoverContent>
        </Popover>
      </div>
      <p className={`mt-1 h-4 text-xs tabular-nums ${resultado != null ? "text-primary font-semibold" : "text-muted-foreground"}`}>
        {conta ? (resultado != null ? `= ${numero(resultado, casas, { minimo: min })}  ·  Enter confirma` : "Conta incompleta") : ""}
      </p>
    </div>
  );
});

export default CampoConta;
